import { createSlice, current } from '@reduxjs/toolkit';
import { USER_ANALYSIS } from '../../actions/userAnalysis/actionTypes';
import {
  normalizeAssessmentLists,
  normalizeAssessorEmployeeListUsers,
  normalizeAssessorFeedbackList,
  asArray
} from '../../../utils/apiPayload';

const initialState = {
  userList: {
    isLoading: false,
    data: {
      users: [],
      assessmentCycle: null,
      assessmentCycles: [],
      evaluationAssessmentCycles:[],
      userAnalysis: null,
      disabledObject: null,
      hideObject: null,
      showObject: null,
      textObject: null,
      assessorFeedback: null,
      valueObject: null,
      experience: {
        score: '',
        primarySkill: null,
        secondarySkill: null
      },
      innovation: {
        score: ''
      },
      contribution: {
        score: ''
      },
      evaluation: {
        score: ''
      },
      technology: {
        score: ''
      },
      finalScore: { score: null, called: false },
      currentCycleFetched: -1,
      assessorFeedbackFetched: -1,
      scoreDetails: null,
      evaluateScoreDetails:null,
      skills: {
        primary: [],
        secondary: []
      },
      contributionTypeQuestions: null,
      innovationTypeQuestions: null,
      currentCycleStatus: null,
      cycleResponseViewModelCollection:[],
      skillobj:[],
      selfAssessmentDataCollection:[]
    },
    errors: '',
    message: '',

  }
};

export const userListSlice = createSlice({
  name: USER_ANALYSIS,
  initialState,
  reducers: {
    getUserListAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getUserListSuccessAction: (state, action) => {
      if (action.payload) {
        const cycleUsers = action.payload?.AssessementCycleUsers;
        state.userList.data.users = normalizeAssessorEmployeeListUsers(asArray(cycleUsers));
        state.userList.data.assessmentCycle = action.payload?.AssessmentCycle;
      }
      state.userList.isLoading = false;
    },
    getUserListFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getCycleListAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getCycleListSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.assessmentCycles = action.payload;
      }
      state.userList.isLoading = false;
    },
    getCycleListFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getEvaluationCycleListAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getEvaluationCycleListSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.evaluationAssessmentCycles = action.payload;
      }
      state.userList.isLoading = false;
    },
    getEvaluationCycleListFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getSelfCycleListAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getSelfCycleListSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.cycleResponseViewModelCollection = action.payload?.cycleResponseViewModelCollection;
        state.userList.data.skillobj = action.payload?.skillobj;
      }
      state.userList.isLoading = false;
    },
    getSelfCycleListFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },

    getTakeSelfAssessmentAction: (state,action) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getTakeSelfAssessmentSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.selfAssessmentDataCollection = action.payload;
      }
      state.userList.isLoading = false;
    },
    getTakeSelfAssessmentFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = '';
    },
    getUserAnalysisAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getUserAnalysisSuccessAction: (state, action) => {
      if (action.payload) {
        let analysisObj = normalizeAssessmentLists(action.payload);

        // Backend returns PascalCase keys — normalise to camelCase so all downstream
        // code that reads e.g. technologyWeightage continues to work unchanged.
        const norm = (pascal, camel) => analysisObj[pascal] ?? analysisObj[camel];

        const technologyWeightage    = norm('TechnologyWeightage',   'technologyWeightage');
        const experienceWeightage    = norm('ExperienceWeightage',   'experienceWeightage');
        const innovationWeightage    = norm('InnovationWeightage',   'innovationWeightage');
        const contributionWeightage  = norm('ContributionWeightage', 'contributionWeightage');
        const evaluationWeightage    = norm('EvaluationWeightage',   'evaluationWeightage');

        // Normalise every PascalCase key to camelCase so component/state reads work
        analysisObj = {
          ...analysisObj,
          technologyWeightage,
          experienceWeightage,
          innovationWeightage,
          contributionWeightage,
          evaluationWeightage,
          technologyVisibility:   norm('TechnologyVisibility',   'technologyVisibility'),
          experienceVisibility:   norm('ExperienceVisibility',   'experienceVisibility'),
          innovationVisibility:   norm('InnovationVisibility',   'innovationVisibility'),
          contributionVisibility: norm('ContributionVisibility', 'contributionVisibility'),
          evaluationVisibility:   norm('EvaluationVisibility',   'evaluationVisibility'),
          cycleId:                norm('CycleId',                'cycleId'),
          cycleName:              norm('CycleName',              'cycleName'),
          cycleType_Id:           norm('CycleType_Id',           'cycleType_Id'),
          currentUserSamId:       norm('CurrentUserSamId',       'currentUserSamId'),
          skillPrefrence:         norm('SkillPrefrence',         'skillPrefrence'),
          secondarySkillPrefrence:norm('SecondarySkillPrefrence','secondarySkillPrefrence'),
          username:               norm('Username',               'username'),
          cycleResponseId:        norm('CycleResponseId',        'cycleResponseId'),
          skillArea:              norm('SkillArea',              'skillArea'),
          primarySkills:          norm('PrimarySkills',          'primarySkills'),
          contributionTypeQuestions: norm('ContributionTypeQuestions', 'contributionTypeQuestions'),
          innovationTypeQuestions:   norm('InnovationTypeQuestions',   'innovationTypeQuestions'),
          primaryAssessmentList:  analysisObj.primaryAssessmentList ?? norm('PrimaryAssessmentList',  'primaryAssessmentList'),
          secondaryAssessmentList: analysisObj.secondaryAssessmentList ?? norm('SecondaryAssessmentList','secondaryAssessmentList'),
          isAssessor:             norm('IsAssessor',             'isAssessor'),
          isAssessorWindow:       norm('IsAssessorWindow',       'isAssessorWindow'),
          isUserWindow:           norm('IsUserWindow',           'isUserWindow'),
          attachment:             norm('Attachment',             'attachment'),
          fileTypes:              norm('FileTypes',              'fileTypes'),
        };

        if (technologyWeightage) {
          const technologyWeightageOptions = [];
          Array.from(Array(Number(technologyWeightage) + 1)).forEach((_, i) => {
            technologyWeightageOptions.push(i);
          });
          analysisObj = { ...analysisObj, technologyWeightageOptions };
        }
        if (experienceWeightage) {
          const experienceWeightageOptions = [];
          Array.from(Array(Number(experienceWeightage) + 1)).forEach((_, i) => {
            if (i % 5 === 0) {
              experienceWeightageOptions.push(i);
            }
          });
          analysisObj = { ...analysisObj, experienceWeightageOptions };
        }
        if (innovationWeightage) {
          const innovationWeightageOptions = [];
          Array.from(Array(Number(innovationWeightage) + 1)).forEach((_, i) => {
            if (i % 5 === 0) {
              innovationWeightageOptions.push(i);
            }
          });
          analysisObj = { ...analysisObj, innovationWeightageOptions };
        }
        if (contributionWeightage) {
          const contributionWeightageOptions = [];
          Array.from(Array(Number(contributionWeightage) + 1)).forEach((_, i) => {
            if (i % 5 === 0) {
              contributionWeightageOptions.push(i);
            }
          });
          analysisObj = { ...analysisObj, contributionWeightageOptions };
        }
        if (evaluationWeightage) {
          const evaluationWeightageOptions = [];
          Array.from(Array(Number(evaluationWeightage) + 1)).forEach((_, i) => {
            if (i % 5 === 0) {
              evaluationWeightageOptions.push(i);
            }
          });
          analysisObj = { ...analysisObj, evaluationWeightageOptions };
        }
        state.userList.data.contributionTypeQuestions = analysisObj.contributionTypeQuestions;
        state.userList.data.innovationTypeQuestions = analysisObj.innovationTypeQuestions;
        state.userList.data.userAnalysis = analysisObj;
      }
      state.userList.isLoading = false;
    },
    getUserAnalysisFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getCurrentCycleStatusAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getCurrentCycleStatusSuccessAction: (state, { payload }) => {
      const { userAnalysis } = current(state.userList.data);
      const status = Number(payload);
      const cycleStatus = Number.isFinite(status) ? status : 0;
      let disabledObject = {};
      let hideObj = {};
      let textObj = {};
      if (userAnalysis?.isAssessor) {
        disabledObject = { ...disabledObject, userDivInput: true };
        hideObj = { ...hideObj, fileUpload: true };
        textObj = { ...textObj, acSubmit: 'Submit Review' };

        let technologyPercentage = null;
        if (userAnalysis?.primaryAssessmentList?.length) {
          const examScore = userAnalysis?.primaryAssessmentList[0]?.userScore;
          const technologyMaxWeightage = userAnalysis?.technologyWeightage;
          technologyPercentage = Math.round(
            (parseFloat(technologyMaxWeightage) * parseFloat(examScore)) / parseFloat(100)
          );
          state.userList.data.technology.score = technologyPercentage;
          disabledObject = { ...disabledObject, ddlTechnology: true };
        }

        if (cycleStatus === 4) {
          disabledObject = { ...disabledObject, assessorDivInput: true };
          hideObj = { ...hideObj, deleteQuestionnaireLink: true, startQuestionnaireLink: true, acSubmit: true, acSave: true };
        } else {
          if (technologyPercentage != null) {
            state.userList.data.finalScore.score = technologyPercentage;
          }
          disabledObject = { ...disabledObject, assessorDivInput: false };
          hideObj = { ...hideObj, deleteQuestionnaireLink: true, startQuestionnaireLink: false, acSubmit: false, acSave: false };
        }
        if (cycleStatus < 2) {
          disabledObject = { ...disabledObject, assessorDivInput: true };
          hideObj = { ...hideObj, assessorDiv: true };
        } else {
          disabledObject = { ...disabledObject, evaluationExpand: false };
          hideObj = { ...hideObj, assessorDiv: false };
        }
      } else {
        textObj = { ...textObj, acSubmit: 'Submit For Review' };
        if (cycleStatus >= 2) {
          disabledObject = { ...disabledObject, userDivInput: true };
          hideObj = { ...hideObj, deleteQuestionnaireLink: true, startQuestionnaireLink: true, acSubmit: true, acSave: true };
        } else {
          disabledObject = { ...disabledObject, assessorDivInput: false };
          hideObj = { ...hideObj, deleteQuestionnaireLink: false, startQuestionnaireLink: false, acSubmit: false, acSave: false };
        }
        if (cycleStatus === 4) {
          // LoadAssessorFeedbacks(); // api call
          disabledObject = { ...disabledObject, assessorDivInput: true, evaluationExpand: false };
          hideObj = { ...hideObj, assessorDiv: false };
        } else {
          disabledObject = { ...disabledObject, assessorDivInput: true };
          hideObj = { ...hideObj, assessorDiv: true };
        }
      }
      disabledObject = { ...disabledObject, ddlTechnology: true };
      state.userList.data.disabledObject = disabledObject;
      state.userList.data.hideObject = hideObj;
      state.userList.data.textObject = textObj;
      state.userList.data.currentCycleStatus = cycleStatus;
      state.userList.isLoading = false;
      state.userList.data.currentCycleFetched = 1;
    },
    getCurrentCycleStatusFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getAssessorFeedbackAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getAssessorFeedbackSuccessAction: (state, action) => {
      if (action.payload) {
        const feedbackList = normalizeAssessorFeedbackList(action.payload);
        state.userList.data.assessorFeedback = feedbackList;
        let finalScore = 0;
        const { experience, innovation, userAnalysis, contribution, evaluation, technology, showObject, disabledObject, valueObject } =
          current(state.userList.data);
        if (feedbackList.length) {
          let valueObj = { ...valueObject };
          feedbackList.forEach((obj) => {
            if (obj.assessmentCategory_Id === 1) {
              finalScore += obj.assessorWeightage;
              valueObj = { ...valueObj, txtTechnology: obj.assessorComments, ddlTechnology: obj.assessorWeightage };
              state.userList.data.technology.score = obj.assessorWeightage;
            } else if (obj.assessmentCategory_Id === 2) {
              finalScore += obj.assessorWeightage;
              valueObj = { ...valueObj, txtExperience: obj.assessorComments };
              if (!userAnalysis.experienceWeightageOptions.includes(obj.assessorWeightage)) {
                state.userList.data.userAnalysis.experienceWeightageOptions = [
                  ...userAnalysis.experienceWeightageOptions,
                  obj.assessorWeightage
                ];
              }
              state.userList.data.experience.score = obj.assessorWeightage;
            } else if (obj.assessmentCategory_Id === 3) {
              finalScore += obj.assessorWeightage;
              valueObj = { ...valueObj, txtInnovation: obj.assessorComments };
              if (!userAnalysis.innovationWeightageOptions.includes(obj.assessorWeightage)) {
                state.userList.data.userAnalysis.innovationWeightageOptions = [
                  ...userAnalysis.innovationWeightageOptions,
                  obj.assessorWeightage
                ];
              }
              state.userList.data.innovation.score = obj.assessorWeightage;
            } else if (obj.assessmentCategory_Id === 4) {
              finalScore += obj.assessorWeightage;
              valueObj = { ...valueObj, txtContribution: obj.assessorComments };
              if (!userAnalysis.contributionWeightageOptions.includes(obj.assessorWeightage)) {
                state.userList.data.userAnalysis.contributionWeightageOptions = [
                  ...userAnalysis.contributionWeightageOptions,
                  obj.assessorWeightage
                ];
              }
              state.userList.data.contribution.score = obj.assessorWeightage;
            } else if (obj.assessmentCategory_Id === 5) {
              finalScore += obj.assessorWeightage;
              valueObj = { ...valueObj, txtEvaluation: obj.assessorComments };
              if (!userAnalysis.evaluationWeightageOptions.includes(obj.assessorWeightage)) {
                state.userList.data.userAnalysis.evaluationWeightageOptions = [
                  ...userAnalysis.evaluationWeightageOptions,
                  obj.assessorWeightage
                ];
              }
              state.userList.data.evaluation.score = obj.assessorWeightage;
            }
          });
          state.userList.data.showObject = { ...(showObject || {}), scoreBoard: true };
          state.userList.data.finalScore.score = finalScore;
          state.userList.data.valueObject = valueObj;
        } else {
          if (userAnalysis?.primaryAssessmentList?.length) {
            let score = 0;
            score = userAnalysis?.primaryAssessmentList[0]?.userScore;
            const technologyMaxWeightage = userAnalysis?.technologyWeightage;
            let percentage = (parseFloat(technologyMaxWeightage) * parseFloat(score)) / parseFloat(100);
            percentage = Math.round(percentage);
            state.userList.data.finalScore.score = percentage;
            state.userList.data.technology.score = percentage;
            state.userList.data.disabledObject = { ...disabledObject, ddlTechnology: true };
            finalScore = percentage;
          } else {
            finalScore = technology.score;
          }
          state.userList.data.showObject = { ...(showObject || {}), scoreBoard: true };
        }
        state.userList.data.finalScore.called = true;
        state.userList.data.currentCycleFetched = 0;
        state.userList.data.assessorFeedbackFetched = 1;

        // GetUserProficiencyLevel(FinalScore);
      }
      state.userList.isLoading = false;
    },
    getAssessorFeedbackFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getUserProficiencyLevelAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getUserProficiencyLevelSuccessAction: (state, action) => {
      if (action.payload) {
        const { showObject, valueObject } = current(state.userList.data);
        // state.userList.data.userAnalysis = action.payload;
        state.userList.data.showObject = { ...showObject, proficencyBoard: true };
        state.userList.data.valueObject = { ...valueObject, txtProficency: action.payload };
      }
      state.userList.data.assessorFeedbackFetched = 0;
      state.userList.data.finalScore.called = false;
      state.userList.isLoading = false;
    },
    getUserProficiencyLevelFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getScoreDetailsAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getScoreDetailsSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.scoreDetails = action.payload;
      }
      state.userList.isLoading = false;
    },
    getScoreDetailsFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getEvaluateScoreDetailsAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    getEvaluateScoreDetailsSuccessAction: (state, action) => {
      if (action.payload) {
        state.userList.data.evaluateScoreDetails = action.payload;
      }
      state.userList.isLoading = false;
    },
    getEvaluateScoreDetailsFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    getEvaluateResetScoreListAction: (state) => {
        state.userList.data.evaluateScoreDetails = []
      state.userList.isLoading = false;
    },
    getCurrentResponseDataAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
      state.userList.data.userAnalysis.isCurrentResponseDataFetched = true;
    },
    getCurrentResponseDataSuccessAction: (state, { payload }) => {
      const getSkill = (skillsList, skillId) => {
        for (let skillItem of skillsList) {
          if (skillItem.id === skillId) {
            return skillItem.skillName;
          } else {
            if (skillItem.secondarySkill?.length) {
              getSkill(skillItem.secondarySkill, skillId);
            }
          }
        }
      };
      if (payload) {
        const { valueObject, hideObject, currentCycleStatus, userAnalysis } = current(state.userList.data);
        const primary = [],
          secondary = [];
        if (payload.categoryUserExperience !== null ||payload.categoryUserExperienceDetail!== null) {
          const newValObj = {
            txtTraining: userAnalysis?.isAssessor?payload.categoryUserExperienceDetail?.userTrainingsDetails:payload.categoryUserExperience?.userTrainingsDetails,
            txtCertification:userAnalysis?.isAssessor?payload.categoryUserExperienceDetail?.userCertificationDetails: payload.categoryUserExperience?.userCertificationDetails
          };
          state.userList.data.valueObject = { ...valueObject, ...newValObj };
        }
        if (payload.categoryUserExperienceDetail?.length) {
          payload.categoryUserExperienceDetail?.forEach((category, index) => {
            if (index === 0) {
              if (category.skill_Id > 0) {
                primary.push({
                  skillId: category.skill_Id,
                  txtVersion: category.version,
                  txtLastUsed: category.lastUsed,
                  txtYears: category.experienceYears,
                  txtMonths: category.experienceMonths,
                  assessmentCycleResponseId: userAnalysis?.isAssessor&&category.assessmentCycleResponse_Id,
                  id: category.id,
                });
              } else {
                primary.push({ skillId: category.skill_Id });
              }
            } else {
              if (category.skill_Id > 0) {
                const skillName = getSkill(userAnalysis?.skillArea, category.skill_Id);
                secondary.push({
                  skillId: category.skill_Id,
                  txtVersion: category.version,
                  txtLastUsed: category.lastUsed,
                  txtYears: category.experienceYears,
                  txtMonths: category.experienceMonths,
                  assessmentCycleResponseId:userAnalysis?.isAssessor&& category.assessmentCycleResponse_Id,
                  id: userAnalysis?.isAssessor&& category.id,
                  skillName
                });
              } else {
                secondary.push({ skillId: category.skill_Id });
              }
            }
          });
          if (primary.length) state.userList.data.skills.primary = primary;
          if (secondary.length) state.userList.data.skills.secondary = secondary;
        } else {
          if (userAnalysis?.isAssessor) {
            state.userList.data.hideObject = { ...hideObject, primarySkill: true, secondarySkill: true };
          } else {
            if (currentCycleStatus >= 2) {
              state.userList.data.hideObject = { ...hideObject, primarySkill: true, secondarySkill: true };
            }

            if (userAnalysis.secondarySkillPrefrence > 0) {
              state.userList.data.skills.secondary = [
                {
                  skillId: userAnalysis.secondarySkillPrefrence,
                  txtVersion: '',
                  txtLastUsed: '',
                  txtYears: '',
                  txtMonths: ''
                }
              ];
            }
            // needs checking
            if (userAnalysis.skillPrefrence > 0) {
              state.userList.data.skills.primary = [
                {
                  skillId: userAnalysis.skillPrefrence,
                  txtVersion: '',
                  txtLastUsed: '',
                  txtYears: '',
                  txtMonths: ''
                }
              ];
            }
          }
        }

        if (payload.assessmentCategoryQuestionAnswer?.length) {
          let newContributions = [...userAnalysis?.contributionTypeQuestions];
          let newInnovations = [...userAnalysis?.innovationTypeQuestions];
          let isContribution = false,
            isInnovation = false;
          payload.assessmentCategoryQuestionAnswer.map((answer) => {
            if (answer.assessmentCategory_Id === 4) {
              const index = userAnalysis?.contributionTypeQuestions.findIndex((val) => val?.id === answer?.categoryQuestion_Id);
              if (index > -1) {
                isContribution = true;
                newContributions[index] = { ...newContributions[index], answer: answer?.answer };
              }
            } else if (answer.assessmentCategory_Id === 3) {
              const index = userAnalysis?.innovationTypeQuestions.findIndex((val) => val?.id === answer?.categoryQuestion_Id);
              if (index > -1) {
                isInnovation = true;
                newInnovations[index] = { ...newInnovations[index], answer: answer?.answer };
              }
            }
          });
          if (isContribution) {
            state.userList.data.userAnalysis.contributionTypeQuestions = newContributions;
            state.userList.data.contributionTypeQuestions = newContributions;
          }
          if (isInnovation) {
            state.userList.data.userAnalysis.innovationTypeQuestions = newInnovations;
            state.userList.data.innovationTypeQuestions = newInnovations;
          }
        }
      }
      state.userList.isLoading = false;
    },
    getCurrentResponseDataFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    saveResponseAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    saveResponseSuccessAction: (state, { payload }) => {
      if (payload.data > 0) {
        if (payload.type === 'Saved') {
          state.userList.message = 'Saved Successfully !';
        } else {
          state.userList.message = 'Submitted Successfully !';
          setTimeout(function () {
            location.reload();
          }, 1000);
        }
      }
      state.userList.isLoading = false;
    },
    saveResponseFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = 'Evaluation form Save/Submit Failed.Please check the details you entered.';
    },

    
    resetUserListAction: (state) => {
      state.userList.message = '';
      state.userList.errors = '';
    },
    resetUserListValueAction: (state, { payload }) => {
      if (payload && typeof payload === 'object' && payload.key) {
        const parts = String(payload.key).split('.');
        let target = state.userList.data;
        for (let i = 0; i < parts.length - 1; i += 1) {
          const part = parts[i];
          if (target[part] == null || typeof target[part] !== 'object') {
            target[part] = {};
          }
          target = target[part];
        }
        target[parts[parts.length - 1]] = payload.value;
        return;
      }
      if (typeof payload === 'string' && payload) {
        state.userList.data[payload] = initialState.userList.data[payload];
      }
    },
    updateScore: (state, { payload }) => {
      if (payload?.type == null) return;
      if (payload.value === 0 || payload.value === '0' || payload.value) {
        state.userList.data[payload.type].score = payload.value;
      }
    },
    handleSecondarySkills: (state, { payload }) => {
      const { skills } = current(state.userList.data);
      if (payload) {
        const newSkills = [...skills.secondary].removeIndexedItem(payload);
        state.userList.data.skills.secondary = newSkills;
      } else {
        if (skills.secondary.length < 5) {
          state.userList.data.skills.secondary = [
            ...skills.secondary,
            {
              skillId: '',
              txtVersion: '',
              txtLastUsed: '',
              txtYears: '',
              txtMonths: '',
              label: ''
            }
          ];
        }
      }
    },
    handleSkillsAction: (state, { payload }) => {
      const { skills, userAnalysis } = current(state.userList.data);
      if (payload) {
        const { type, key, value, index, label } = payload;
        if (type === 'primary') {
          if (!skills.primary.length && userAnalysis.skillPrefrence) {
            let pSkill = {
              skillId: userAnalysis.skillPrefrence,
              txtVersion: '',
              txtLastUsed: '',
              txtYears: '',
              txtMonths: ''
            };
            pSkill[key] = value;
            state.userList.data.skills[type] = [pSkill];
          } else {
            state.userList.data.skills[type][index][key] = value;
          }
        } else {
          if (!skills.secondary[index] && userAnalysis.secondarySkillPrefrence) {
            let sSkill = {
              skillId: userAnalysis.secondarySkillPrefrence,
              txtVersion: '',
              txtLastUsed: '',
              txtYears: '',
              txtMonths: '',
              label: ''
            };
            sSkill[key] = value;
            if (key === 'skillId') sSkill['label'] = label;
            state.userList.data.skills[type][index] = sSkill;
          } else {
            if (key === 'skillId') state.userList.data.skills[type][index]['label'] = label;
            state.userList.data.skills[type][index][key] = value;
          }
        }
      }
    },
    handleValueObject: (state, { payload }) => {
      if (payload) {
        const { key, value } = payload;
        state.userList.data.valueObject[key] = value;
      }
    },
    handleSetErrorAction: (state, { payload }) => {
      state.userList.errors = payload;
    },
    handleAnswerAction: (state, { payload }) => {
      if (payload) {
        const { key, index, value } = payload;
        state.userList.data[key][index].answer = value;
      }
    },
    deleteAttachmentAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    deleteAttachmentSuccessAction: (state) => {
      state.userList.message = 'File Deleted Successfully!';
      state.userList.isLoading = false;
      window.location.reload();
    },
    deleteAttachmentFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },


    downloadAttachmentAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    downloadAttachmentSuccessAction: (state, { payload }) => {
      // state.userList.message = 'File Deleted Successfully!';
      const byteCharacters = atob(payload.fileContents);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: payload.contentType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', payload.fileDownloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();

      state.userList.isLoading = false;
    },
    downloadAttachmentFailureAction: (state, action) => {
      state.userList.isLoading = false;
      state.userList.errors = action.payload;
    },
    resetExamAction: (state) => {
      state.userList.isLoading = true;
      state.userList.errors = '';
    },
    resetExamSuccessAction: (state) => {
      state.userList.isLoading = false;
      state.userList.message = 'Assessment Reset Completed Successfully!';
      setTimeout(function () {
        location.reload();
      }, 1000);
      // window.location.reload();
    },
    resetExamFailureAction: (state, { payload }) => {
      state.userList.isLoading = false;
      state.userList.errors = payload;
    }
  }
});

export const {
  getUserListAction,
  getUserListFailureAction,
  getUserListSuccessAction,
  resetUserListAction,
  getCycleListAction,
  getCycleListFailureAction,
  getCycleListSuccessAction,
  getEvaluationCycleListAction,
  getEvaluationCycleListFailureAction,
  getEvaluationCycleListSuccessAction,
  getUserAnalysisAction,
  getUserAnalysisFailureAction,
  getUserAnalysisSuccessAction,
  getCurrentCycleStatusAction,
  getCurrentCycleStatusFailureAction,
  getCurrentCycleStatusSuccessAction,
  getAssessorFeedbackAction,
  getAssessorFeedbackFailureAction,
  getAssessorFeedbackSuccessAction,
  getUserProficiencyLevelAction,
  getUserProficiencyLevelFailureAction,
  getUserProficiencyLevelSuccessAction,
  getScoreDetailsAction,
  getScoreDetailsFailureAction,
  getScoreDetailsSuccessAction,
  getEvaluateScoreDetailsAction,
  getEvaluateScoreDetailsSuccessAction,
  getEvaluateScoreDetailsFailureAction,
  getCurrentResponseDataAction,
  getCurrentResponseDataFailureAction,
  getCurrentResponseDataSuccessAction,
  resetUserListValueAction,
  saveResponseAction,
  saveResponseFailureAction,
  saveResponseSuccessAction,
  updateScore,
  handleSecondarySkills,
  handleSkillsAction,
  handleValueObject,
  handleSetErrorAction,
  handleAnswerAction,
  deleteAttachmentAction,
  deleteAttachmentFailureAction,
  deleteAttachmentSuccessAction,
  downloadAttachmentAction,
  downloadAttachmentFailureAction,
  downloadAttachmentSuccessAction,
  resetExamAction,
  resetExamFailureAction,
  resetExamSuccessAction,
  getSelfCycleListAction,
  getSelfCycleListSuccessAction,
  getSelfCycleListFailureAction,
  getTakeSelfAssessmentAction,
  getTakeSelfAssessmentSuccessAction,
  getTakeSelfAssessmentFailureAction,
  getEvaluateResetScoreListAction
} = userListSlice.actions;

export default userListSlice.reducer;
